import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import PackersNumpad from "@/components/packers/PackersNumpad";
import PackersToolbar from "@/components/packers/PackersToolbar";
import PackerOrderCard from "@/components/packers/PackerOrderCard";
import { usePackersList, usePrintLabel } from "@/hooks/usePackers";
import type { PackerOrder } from "@/hooks/usePackers";
import { generateEExactXml, downloadEExactXml } from "@/services/export/eExactXmlGenerator";
import { applyCorrections } from "@/hooks/usePackerCorrectionsCache";
import type { EExactOrder } from "@/services/export/eExactXmlGenerator";
import { extractSKUs } from "@/utils/skuExtractor";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";

/** An order is "new / eligible for download" when:
 *  1. Not yet printed
 *  2. Not scheduled for future shipping
 *  3. Every non-ignored line item has a base_paint match */
const isEligibleNew = (order: PackerOrder): boolean => {
  if (order.is_printed || order.is_future_shipping) return false;
  const relevant = order.line_items.filter(li => !li.base_paint?.ignored_brand);
  if (relevant.length === 0) return false;
  return relevant.every(li => li.base_paint != null);
};

const Packers = () => {
  const [numpadFilter, setNumpadFilter] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { hasPermission } = usePermissions();
  const canWrite = hasPermission("kleurenOrders", "write");

  const { data, isLoading, refetch } = usePackersList({ limit: 200 });
  const printLabel = usePrintLabel();

  const hasRelevantItems = (order: PackerOrder): boolean =>
    order.line_items.some(li => !li.base_paint?.ignored_brand);

  const orders = applyCorrections((data?.data || []).filter(hasRelevantItems));
  const meta = data?.meta || { total: 0, limit: 0, offset: 0, packed_today: 0, recently_cancelled: [], recently_shipped: [] };

  useEffect(() => {
    if (meta.recently_cancelled?.length) {
      meta.recently_cancelled.forEach(nr => toast.warning(`Order ${nr} is geannuleerd`));
    }
    if (meta.recently_shipped?.length) {
      meta.recently_shipped.forEach(nr => toast.info(`Order ${nr} is verzonden`));
    }
  }, [JSON.stringify(meta.recently_cancelled), JSON.stringify(meta.recently_shipped)]);

  const filteredOrders = useMemo(() => {
    if (!numpadFilter) return orders;
    return orders.filter((o) => o.ordernumber.endsWith(numpadFilter));
  }, [orders, numpadFilter]);

  const toggleSelect = useCallback((ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  }, []);

  const selectAll = () => {
    if (selected.size === filteredOrders.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredOrders.map((o) => o.ordernumber)));
    }
  };

  const printSelected = () => {
    if (selected.size === 0) return;
    const ordernumbers = [...selected];
    printLabel.mutate(ordernumbers, {
      onSuccess: () => {
        setSelected(new Set());
      },
    });
  };

  const handlePrintLabel = (ordernumber: string) => {
    printLabel.mutate([ordernumber]);
  };

  const printNew = () => {
    const eligible = orders.filter(isEligibleNew).map((o) => o.ordernumber);
    if (eligible.length === 0) return;
    printLabel.mutate(eligible, {
      onSuccess: () => {
        setSelected(new Set());
      },
    });
  };

  /** Get booking product_id: explicit match first, then KX→WN SKU fallback */
  const getBookingProductId = (li: PackerOrder['line_items'][number]): string | null => {
    if (li.base_paint?.booking_product?.product_id) {
      return li.base_paint.booking_product.product_id;
    }
    // Fallback: derive from line item SKU (KX → WN conversion)
    if (li.sku) {
      const skus = extractSKUs(li.sku);
      for (const sku of skus) {
        if (sku.includes('KX')) {
          return sku.replace('KX', 'WN');
        }
      }
      // Return first extracted SKU if any
      if (skus.length > 0) return skus[0];
    }
    return null;
  };

  const handleExportXml = () => {
    const selectedOrders = orders.filter(o => selected.has(o.ordernumber));
    const eExactOrders: EExactOrder[] = selectedOrders.map(order => ({
      ordernumber: order.ordernumber,
      lines: order.line_items
        .filter(li => !li.base_paint?.ignored_brand && getBookingProductId(li) != null)
        .map(li => ({
          itemCode: getBookingProductId(li)!,
          text: [
            li.producttitle,
            [li.color_code, li.collection_name].filter(Boolean).join(' - ')
          ].filter(Boolean).join(' | '),
          quantity: li.quantity,
        })),
    })).filter(o => o.lines.length > 0);

    if (eExactOrders.length === 0) {
      toast.error('Geen orders met boekingsproducten gevonden');
      return;
    }

    const skipped = selectedOrders.length - eExactOrders.length;
    try {
      const xml = generateEExactXml(eExactOrders);
      downloadEExactXml(xml);
      let msg = `XML geëxporteerd voor ${eExactOrders.length} order(s)`;
      if (skipped > 0) msg += ` (${skipped} overgeslagen zonder productmatch)`;
      toast.success(msg);
    } catch (e) {
      toast.error('Fout bij genereren XML bestand');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a1f3d] to-[#0f1221] p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">Kleuren Orders</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => refetch()}
          className="text-white/60 hover:text-white hover:bg-white/10"
        >
          <RefreshCw className="w-5 h-5" />
        </Button>
      </div>

      {/* Numpad */}
      <PackersNumpad filter={numpadFilter} onFilterChange={setNumpadFilter} />

      {/* Toolbar */}
      <PackersToolbar
        total={meta.total}
        packedToday={meta.packed_today}
        selectedCount={selected.size}
        newCount={orders.filter(isEligibleNew).length}
        onSelectAll={selectAll}
        onPrintSelected={printSelected}
        onPrintNew={printNew}
        onExportXml={handleExportXml}
        isPrinting={printLabel.isPending}
        canWrite={canWrite}
      />

      {/* Orders */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-20 text-white/40">
          {numpadFilter
            ? `Geen orders eindigend op ...${numpadFilter}`
            : "Geen orders beschikbaar"}
        </div>
      ) : (
        <div className="space-y-3 pb-20">
          {filteredOrders.map((order) => (
            <PackerOrderCard
              key={order.ordernumber}
              order={order}
              selected={selected.has(order.ordernumber)}
              onToggleSelect={toggleSelect}
              onPrintLabel={handlePrintLabel}
              isPrintingLabel={printLabel.isPending}
              canWrite={canWrite}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Packers;
